// P1.6 — GDPR consent banner.
//
// Renders a non-blocking footer bar on first visit (Accept all / Reject all
// / Customize). Once chosen, authenticated users reopen preferences from
// navigation or their account menu; public pages retain an inline button.
//
// Mounting: rendered once at the top of `App.tsx`, *outside* `RequireAuth`,
// so it appears on public routes (login/register) too — required for an
// EU-compliant first contact.

import { useCallback, useEffect, useRef, useState } from "react";
import { PUBLIC_SITE_URL } from "../../config/constants";
import {
  ACCEPT_ALL,
  ALL_DENIED,
  TOGGLEABLE_CATEGORIES,
  type ConsentChoice,
  type ToggleableCategory,
  readConsent,
  writeConsent,
  OPEN_CONSENT_EVENT,
} from "../../utils/consent";
import styles from "./ConsentBanner.module.css";

const CATEGORY_LABELS: Record<
  ToggleableCategory,
  { title: string; description: string }
> = {
  analytics_storage: {
    title: "Analytics",
    description:
      "Lets Google Analytics measure which features are used so we can improve the product. Data is shared with Google. We use no advertising or cross-site tracking cookies.",
  },
};

export function ConsentBanner({
  authenticated = false,
}: {
  authenticated?: boolean;
}) {
  // `decided` toggles the banner off (and the public re-open button on)
  // without losing the user's draft selection in `selection`.
  const [decided, setDecided] = useState<boolean>(() => readConsent() !== null);
  const [showCustomize, setShowCustomize] = useState<boolean>(false);
  const [selection, setSelection] = useState<ConsentChoice>(() => {
    const persisted = readConsent();
    if (persisted) {
      return {
        analytics_storage: persisted.analytics_storage,
        // A record written before this change (or tampered with directly)
        // may carry ad_* as 'granted'. There is no UI path to grant them, so
        // never seed draft state with anything but denied for these three —
        // see the matching enforcement in `persist` below.
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      };
    }
    return { ...ALL_DENIED };
  });
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const open = () => {
      openerRef.current =
        document.activeElement instanceof HTMLElement &&
        document.activeElement !== document.body
          ? document.activeElement
          : document.querySelector<HTMLElement>(
              '[aria-label="User menu"], [aria-label="Open cookie preferences"]',
            );
      setDecided(false);
      setShowCustomize(false);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, open);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, open);
  }, []);

  // Focus management: when the banner appears, focus the first action so
  // keyboard users can act immediately. We don't trap focus (the banner is
  // intentionally non-modal so it doesn't block app usage), but we do honor
  // Escape on the customize panel as a "back to summary" affordance.
  useEffect(() => {
    if (decided) return;
    const node = dialogRef.current?.querySelector<HTMLElement>(
      "[data-consent-default-focus]",
    );
    node?.focus({ preventScroll: true });
  }, [decided, showCustomize]);

  const persist = useCallback((choice: ConsentChoice) => {
    // Belt-and-suspenders with the `selection` seeding above: no matter which
    // caller invokes `persist` (Accept all, Reject all, or Save preferences
    // with a draft that traces back to a stale granted record), the three
    // ad_* signals can never leave here as anything but denied — LazySEM has
    // no advertising tags to consume them.
    const enforced: ConsentChoice = {
      ...choice,
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    };
    writeConsent(enforced);
    setSelection(enforced);
    setDecided(true);
    setShowCustomize(false);
    if (openerRef.current)
      requestAnimationFrame(() => {
        (openerRef.current?.isConnected
          ? openerRef.current
          : document.querySelector<HTMLElement>(
              '[aria-label="User menu"], [aria-label="Open cookie preferences"]',
            )
        )?.focus({ preventScroll: true });
        openerRef.current = null;
      });
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && showCustomize) {
      e.stopPropagation();
      setShowCustomize(false);
    }
  };

  if (decided) {
    if (authenticated) return null;
    return (
      <button
        type="button"
        className={styles.inlineTrigger}
        aria-label="Open cookie preferences"
        title="Cookie preferences"
        onClick={(event) => {
          openerRef.current = event.currentTarget;
          setDecided(false);
        }}
      >
        Privacy preferences
      </button>
    );
  }

  return (
    <div
      ref={dialogRef}
      className={styles.banner}
      role="dialog"
      aria-modal="false"
      aria-labelledby="consent-banner-title"
      aria-describedby="consent-banner-body"
      onKeyDown={onKeyDown}
    >
      <div className={styles.summary}>
        <h2 id="consent-banner-title" className={styles.title}>
          We value your privacy
        </h2>
        <p id="consent-banner-body" className={styles.body}>
          We use cookies that are necessary to keep you signed in. With your
          consent we also use Google Analytics to measure how the product is
          used. We do not use advertising cookies. You can change or withdraw
          your choice at any time.{" "}
          <a
            className={styles.policyLink}
            href={`${PUBLIC_SITE_URL}/cookie-policy`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Cookie Policy
          </a>
          {" · "}
          <a
            className={styles.policyLink}
            href={`${PUBLIC_SITE_URL}/privacy-policy`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setShowCustomize((v) => !v)}
          >
            {showCustomize ? "Hide options" : "Customise"}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => persist(ALL_DENIED)}
          >
            Reject all
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            data-consent-default-focus
            onClick={() => persist(ACCEPT_ALL)}
          >
            Accept all
          </button>
        </div>
      </div>

      {showCustomize && (
        <div className={styles.customize}>
          {TOGGLEABLE_CATEGORIES.map((cat) => (
            <label key={cat} className={styles.categoryRow}>
              <input
                type="checkbox"
                checked={selection[cat] === "granted"}
                // The accessible NAME is just the title; the long description
                // is exposed as an accessible DESCRIPTION via aria-describedby
                // instead of being folded into the name (which is what
                // wrapping both spans in this <label> would otherwise do).
                // That keeps "no advertising ... cookies" readable prose in
                // the description without it also becoming part of the
                // checkbox's *name* — the two are different things to ask
                // "does an advertising toggle exist?" about.
                aria-label={CATEGORY_LABELS[cat].title}
                aria-describedby={`consent-category-desc-${cat}`}
                onChange={(e) =>
                  setSelection((prev) => ({
                    ...prev,
                    [cat]: e.target.checked ? "granted" : "denied",
                  }))
                }
              />
              <span className={styles.categoryText}>
                <span className={styles.categoryTitle}>
                  {CATEGORY_LABELS[cat].title}
                </span>
                <span
                  id={`consent-category-desc-${cat}`}
                  className={styles.categoryDesc}
                >
                  {CATEGORY_LABELS[cat].description}
                </span>
              </span>
            </label>
          ))}
          <div className={styles.customizeActions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => persist(selection)}
            >
              Save preferences
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConsentBanner;
